/** Logical-range sync between the price chart and the panes below it.
 *
 *  lightweight-charts v4 has no native panes (those arrived in v5), so each pane
 *  is its own chart instance and they are kept aligned by hand.
 *
 *  Sync is on the **logical** range, which aligns bar INDICES — so it is only
 *  correct when every member plots the same bar set. That is a precondition, not
 *  a detail: the panes are built onto the price chart's exact bar grid (one
 *  point per price bar, whitespace where there's no data) precisely so index i
 *  means the same moment everywhere. Feed a pane its own denser or sparser
 *  series and logical sync will happily scroll it to a different part of history.
 *
 *  Given that precondition, logical beats time-range sync: it stays exact across
 *  weekend and session gaps, for the same reason the drawing code anchors on
 *  `anchorLogical` (see issue #109).
 */
import type { LogicalRange } from "lightweight-charts";

type Listener = (range: LogicalRange) => void;

/** One group per chart tab, so two tabs never fight over each other's range. */
const groups = new Map<string, Map<string, Listener>>();

/** Re-entrancy guard. Applying a range to a pane fires that pane's own
 *  range-change handler, which would publish straight back and ping-pong
 *  forever. While a broadcast is in flight for a group, ignore echoes. */
const broadcasting = new Set<string>();

export function subscribeRange(
  groupId: string,
  memberId: string,
  fn: Listener,
): () => void {
  let g = groups.get(groupId);
  if (!g) {
    g = new Map();
    groups.set(groupId, g);
  }
  g.set(memberId, fn);
  return () => {
    const cur = groups.get(groupId);
    if (!cur) return;
    cur.delete(memberId);
    if (cur.size === 0) groups.delete(groupId);
  };
}

/** Push `range` to every member of the group except the one that sent it. */
export function publishRange(
  groupId: string,
  memberId: string,
  range: LogicalRange | null,
): void {
  if (!range || broadcasting.has(groupId)) return;
  const g = groups.get(groupId);
  if (!g) return;
  broadcasting.add(groupId);
  try {
    g.forEach((fn, id) => {
      if (id === memberId) return;
      try {
        fn(range);
      } catch {
        /* a dead pane must not stop the others updating */
      }
    });
  } finally {
    broadcasting.delete(groupId);
  }
}
